import consumer from "./consumer";
import { hasActiveRegistration } from "../helpers/active_registration";

const HEARTBEAT_MS = 30000;

consumer.subscriptions.create("AppearanceChannel", {
  initialized() {
    this.heartbeat = this.heartbeat.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.scheduleStatusSync = this.scheduleStatusSync.bind(this);

    this.heartbeatTimer = null;
  },

  connected() {
    // Start heartbeats
    this.startHeartbeat();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    document.addEventListener("turbo:load", this.scheduleStatusSync);
    globalThis.addEventListener("pageshow", this.scheduleStatusSync);
    this.statusObserver = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes].some(node =>
        node.nodeType === 1 && (node.matches("[data-presence-user-id]") ||
          node.querySelector("[data-presence-user-id]"))))) this.scheduleStatusSync();
    });
    this.statusObserver.observe(document.documentElement, { childList: true, subtree: true });

    // send an immediate heartbeat
    this.heartbeat();
  },

  disconnected() {
    this.stopHeartbeat();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.stopStatusSync();
  },

  rejected() {
    this.stopHeartbeat();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.stopStatusSync();
  },

  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(this.heartbeat, HEARTBEAT_MS);
  },

  stopHeartbeat() {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  },

  heartbeat() {
    // lightweight, safe
    this.perform("heartbeat");
    this.syncStatuses();
  },

  scheduleStatusSync() {
    clearTimeout(this.statusSyncTimer);
    this.statusSyncTimer = setTimeout(() => this.syncStatuses(), 50);
  },

  syncStatuses() {
    const ids = [...new Set([...document.querySelectorAll("[data-presence-user-id]")]
      .map(element => element.dataset.presenceUserId))];
    for (let offset = 0; offset < ids.length; offset += 200) {
      this.perform("sync_statuses", { user_ids: ids.slice(offset, offset + 200) });
    }
  },

  stopStatusSync() {
    clearTimeout(this.statusSyncTimer);
    this.statusObserver?.disconnect();
    document.removeEventListener("turbo:load", this.scheduleStatusSync);
    globalThis.removeEventListener("pageshow", this.scheduleStatusSync);
  },

  onVisibilityChange() {
    // Optional: when user comes back, ping immediately
    if (document.visibilityState === "visible") this.heartbeat();
  },

  received(data) {
    if (data.action === 'signin') {
      if (hasActiveRegistration()) return;
      const currentTabId = sessionStorage.getItem('auth_sync_tab_id');
      if (data.client_id === currentTabId) {
        return;
      }

      if (document.body.dataset.authenticated !== 'true') {
        this.syncAuthentication();
      }

      return;
    }

    if (data.action === "presence") {
      for (const element of document
        .querySelectorAll(`.user_${data.user_id}_status`)) {
        const indicator = element.firstElementChild;
        if (indicator) indicator.className = data.css_class;
      }
      return;
    }

    if (data.action !== "logout") return;

    this.unsubscribe();

    // A heartbeat from the old socket can arrive after a normal logout. Do
    // not redirect public pages such as registration back to sign in.
    if (document.body.dataset.authenticated !== "true") return;

    globalThis.location.replace(data.redirect_url);
  },

  async syncAuthentication() {
    // Guest notifications are shared by all browsers. Only reload when this
    // browser's cookies confirm that it actually signed in in another tab.
    if (this.checkingAuthentication) return;
    this.checkingAuthentication = true;
    try {
      const response = await fetch('/sessions/status', {
        credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }
      });
      if (!response.ok || response.redirected) return;
      const state = await response.json();
      if (typeof state.session_state === 'string' && state.session_state !== 'signed-out' &&
          document.body.dataset.authenticated !== 'true' && !hasActiveRegistration()) {
        globalThis.location.reload();
      }
    } catch { /* Failed session checks must not interrupt sign in. */ }
    finally { this.checkingAuthentication = false; }
  }
});
